if (!customElements.get('sidepillow-duo-deal')) {
  customElements.define(
    'sidepillow-duo-deal',
    class SidePillowDuoDeal extends HTMLElement {
      constructor() {
        super();

        this.form = document.getElementById(this.dataset.productFormId);
        this.modeInputs = [...this.querySelectorAll('[data-sp-duo-mode]')];
        this.offerShells = [...this.querySelectorAll('[data-sp-duo-offer-shell]')];
        this.details = this.querySelector('[data-sp-duo-details]');
        this.configuration = this.querySelector('[data-sp-duo-configuration]');
        this.message = this.querySelector('[data-sp-duo-message]');
        this.primaryHeading = this.querySelector('[data-sp-duo-primary-heading]');
        this.primaryGroups = [...this.querySelectorAll('[data-sp-duo-primary-group]')];
        this.firstHeightLabel = this.querySelector('[data-sp-duo-first-height-label]');
        this.secondVariants = [...this.querySelectorAll('[data-sp-duo-second-variant]')];
        this.secondStatus = this.querySelector('[data-sp-duo-second-status]');
        this.secondValue = this.querySelector('[data-sp-duo-second-value]');
        this.secondSame = this.querySelector('[data-sp-duo-second-same]');
        this.secondToggle = this.querySelector('[data-sp-duo-second-toggle]');
        this.secondOptions = this.querySelector('[data-sp-duo-second-options]');
        this.highWarning = this.querySelector('[data-sp-duo-high-warning]');
        this.highConfirm = this.querySelector('[data-sp-duo-high-confirm]');
        this.highError = this.querySelector('[data-sp-duo-high-error]');
        this.variants = this.readVariants();
        this.i18n = this.readTranslations();
        this.isBusy = false;
        this.secondHeightTouched = false;
        this.quantityChosen = false;
        this.firstHeightChosen = false;

        if (!this.form || !this.modeInputs.length || !this.offerShells.length || !this.details || !this.secondVariants.length) return;

        this.submitButton = this.form.querySelector('[name="add"]');
        this.submitLabel = this.submitButton?.querySelector('span');
        this.singleButtonLabel = this.dataset.singleButtonLabel || this.submitLabel?.textContent.trim() || this.translate('inCart', 'Add to cart');

        this.modeInputs.forEach((input) => input.addEventListener('change', this.handleModeChange));
        this.secondVariants.forEach((input) => input.addEventListener('change', this.handleSecondVariantChange));
        this.secondToggle?.addEventListener('click', this.handleSecondToggle);
        this.highConfirm?.addEventListener('change', this.handleHighConfirmChange);
        this.form.addEventListener('submit', this.handleFormSubmit, true);
        this.addEventListener('click', this.handleChoiceInteraction);
        this.addEventListener('change', this.handleChoiceInteraction);
        this.addEventListener('keydown', this.handleChoiceKeydown);
        document.addEventListener('click', this.handleHeightHelperChoice, true);
        document.addEventListener('variant:changed', this.handleVariantChange);
        this.bindStickyBar();

        this.syncMode();
        this.syncFirstVariant();
      }

      disconnectedCallback() {
        this.modeInputs.forEach((input) => input.removeEventListener('change', this.handleModeChange));
        this.secondVariants.forEach((input) => input.removeEventListener('change', this.handleSecondVariantChange));
        this.secondToggle?.removeEventListener('click', this.handleSecondToggle);
        this.highConfirm?.removeEventListener('change', this.handleHighConfirmChange);
        this.form?.removeEventListener('submit', this.handleFormSubmit, true);
        this.removeEventListener('click', this.handleChoiceInteraction);
        this.removeEventListener('change', this.handleChoiceInteraction);
        this.removeEventListener('keydown', this.handleChoiceKeydown);
        document.removeEventListener('click', this.handleHeightHelperChoice, true);
        this.stickyButton?.removeEventListener('click', this.handleStickySubmit, true);
        this.stickyForm?.removeEventListener('submit', this.handleStickySubmit, true);
        if (this.stickyLabel && this.stickyOriginalLabel) {
          this.stickyLabel.textContent = this.stickyOriginalLabel;
        }
        if (this.stickyButton) {
          delete this.stickyButton.dataset.spStickyIntent;
          delete this.stickyButton.dataset.spStickyLabel;
          this.stickyButton.type = this.stickyOriginalType || 'submit';
          this.stickyButton.removeAttribute('aria-controls');
          this.stickyButton.removeAttribute('aria-describedby');
        }
        if (this.stickySummary) {
          this.stickySummary.hidden = true;
          this.stickySummary.closest('.sticky-atc-bar')?.removeAttribute('data-sp-sticky-summary-ready');
        }
        document.removeEventListener('variant:changed', this.handleVariantChange);
        if (document.documentElement.dataset.spDuoMode === 'duo') {
          document.documentElement.dataset.spDuoMode = 'single';
        }
      }

      readVariants() {
        try {
          return JSON.parse(this.querySelector('[data-sp-duo-variants]')?.textContent || '[]');
        } catch (_error) {
          return [];
        }
      }

      get selectedMode() {
        return this.modeInputs.find((input) => input.checked)?.value || 'single';
      }

      get isDuo() {
        return this.selectedMode === 'duo';
      }

      get currentVariantId() {
        return Number(this.form?.querySelector('[name="id"]')?.value || 0);
      }

      get secondVariantId() {
        return Number(this.secondVariants.find((input) => input.checked)?.value || 0);
      }

      readTranslations() {
        try {
          return JSON.parse(this.querySelector('[data-sp-duo-i18n]')?.textContent || '{}');
        } catch (_error) {
          return {};
        }
      }

      translate(key, fallback = '') {
        const dutchFallbacks = {
          inCart: 'In winkelwagen',
          quantityDuo: '2 kussens',
          quantitySingle: '1 kussen',
          from: 'Vanaf',
          close: 'Sluiten',
          change: 'Wijzigen',
          secondCloseAria: 'Hoogtekeuzes voor kussen 2 sluiten',
          secondChangeAria: 'Hoogte van kussen 2 wijzigen',
          heightPerPillow: 'Hoogte per kussen',
          height: 'Hoogte',
          chooseHeight: 'Kies een hoogte',
          sameAsFirst: 'Zelfde als kussen 1',
          errorUnavailable: 'De duo-deal is tijdelijk niet beschikbaar. Probeer het later opnieuw.',
          errorOutOfStock: 'Een gekozen hoogte is niet meer op voorraad. Kies een andere hoogte.',
          errorAdd: 'De kussens konden niet worden toegevoegd.',
          errorDiscount: 'De korting kon niet worden toegepast. Er is niets aan je winkelwagen toegevoegd.',
          errorRetry: 'De duo-deal kon niet worden toegevoegd. Probeer het opnieuw.',
          errorVerifyCart: 'Er ging iets mis. Controleer je winkelwagen voordat je het opnieuw probeert.',
          success: '2 kussens toegevoegd met duo-voordeel.',
          errorCartLoad: 'De winkelwagen kon niet worden geladen.',
          errorGeneric: 'Er ging iets mis.',
        };
        const isDutch = (document.documentElement.lang || '').toLowerCase().startsWith('nl');

        return this.i18n?.[key] || (isDutch ? dutchFallbacks[key] : '') || fallback;
      }

      get hasChosenConfiguration() {
        return this.quantityChosen && this.firstHeightChosen;
      }

      handleChoiceInteraction = (event) => {
        // Initialization and programmatic variant syncing are not customer consent.
        if (!event.isTrusted) return;
        const label = event.target.closest?.('label');
        const input = label ? document.getElementById(label.htmlFor) : event.target.closest?.('input');
        if (!input || input.disabled || input.classList.contains('disabled')) return;
        if (input.matches('[data-sp-duo-mode]')) this.quantityChosen = true;
        if (input.matches('input[type="radio"]') && input.closest('variant-selects')) this.firstHeightChosen = true;
        this.syncStickyState();
      };

      handleChoiceKeydown = (event) => {
        // Space on an already-checked radio does not fire change.
        if (event.key === ' ' && event.target.matches?.('input[type="radio"]')) {
          this.handleChoiceInteraction(event);
        }
      };

      handleHeightHelperChoice = (event) => {
        if (!event.isTrusted) return;
        const apply = event.target.closest?.('[data-sp-height-helper-apply]');
        const dialog = apply?.closest('[data-sp-height-helper-dialog]');
        if (!apply || apply.disabled || dialog?.id !== `sp-height-helper-${this.form.id}`) return;
        // The helper is portaled outside this element; its explicit apply is a choice.
        this.firstHeightChosen = true;
        this.syncStickyState();
      };

      scrollToChoices() {
        const target = !this.quantityChosen ? this.querySelector('[data-sp-choice-start]') : this.details;
        if (target) {
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        }
        target?.scrollIntoView({ block: 'start', behavior: this.scrollBehavior });
      }

      syncStickyState() {
        if (!this.stickySummary || !this.stickyButton) return;
        const ready = this.hasChosenConfiguration;
        const label = ready ? (this.dataset.stickyCartLabel || this.translate('inCart', 'Add to cart')) : this.dataset.stickyButtonLabel;
        this.stickyButton.dataset.spStickyIntent = ready ? 'cart' : 'choose';
        this.stickyButton.dataset.spStickyLabel = label;
        this.stickyButton.setAttribute('aria-controls', ready ? this.form.id : this.choiceStartId);
        this.stickyQuantity.textContent = ready
          ? (this.isDuo ? this.translate('quantityDuo', '2 pillows') : this.translate('quantitySingle', '1 pillow'))
          : this.translate('from', 'From');
        this.stickyPrice.textContent = ready && this.isDuo ? this.dataset.stickyDuoPrice : this.dataset.stickySinglePrice;
        if (this.stickyTotalLabel) this.stickyTotalLabel.hidden = !ready;
        if (!ready) {
          // This action navigates to available choices, even if the default is sold out.
          this.stickyButton.disabled = false;
          this.stickyButton.style.pointerEvents = '';
          this.stickyButton.style.opacity = '';
        }
        if (!this.stickyButton.disabled) this.stickyLabel.textContent = label;
      }

      handleModeChange = () => {
        this.syncMode();
        this.clearMessage();

        if (window.dataLayer && this.isDuo) {
          window.dataLayer.push({
            event: 'sp_bundle_selected',
            offer: 'duo_20',
            quantity: 2,
          });
        }
      };

      handleVariantChange = (event) => {
        const variant = event.detail?.variant;
        if (!variant || Number(variant.id) !== this.currentVariantId) return;
        this.syncFirstVariant(Number(variant.id));
        this.syncMode();
      };

      handleSecondVariantChange = () => {
        this.secondHeightTouched = this.secondVariantId !== this.currentVariantId;
        this.syncSecondHeightStatus();
        this.syncSecondHeightWarning();
        this.setSecondOptionsExpanded(false);
        this.secondToggle?.focus({ preventScroll: true });
        this.clearMessage();
      };

      handleSecondToggle = () => {
        if (!this.secondToggle || !this.secondOptions) return;
        const expanded = this.secondToggle.getAttribute('aria-expanded') === 'true';
        this.setSecondOptionsExpanded(!expanded);
      };

      setSecondOptionsExpanded(expanded) {
        if (!this.secondToggle || !this.secondOptions) return;
        this.secondToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        this.secondToggle.textContent = expanded ? this.translate('close', 'Close') : this.translate('change', 'Change');
        this.secondToggle.setAttribute(
          'aria-label',
          expanded
            ? this.translate('secondCloseAria', 'Close the height options for Pillow 2')
            : this.translate('secondChangeAria', 'Change the height of Pillow 2')
        );
        this.secondOptions.hidden = !expanded;
      }

      handleStickySubmit = (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.isBusy) return;
        if (!this.hasChosenConfiguration) {
          this.scrollToChoices();
          return;
        }
        if (!this.submitButton || this.submitButton.disabled
          || this.submitButton.getAttribute('aria-disabled') === 'true'
          || this.submitButton.classList.contains('btn--loading')) return;
        // Both single and duo use the main form, including height validation.
        this.submitButton.click();
      };

      handleHighConfirmChange = () => {
        if (!this.highConfirm) return;
        this.highConfirm.setAttribute('aria-invalid', 'false');
        if (this.highError) this.highError.hidden = true;
      };

      syncMode() {
        const expanded = this.isDuo;
        this.configuration.hidden = !expanded;
        this.dataset.mode = expanded ? 'duo' : 'single';
        document.documentElement.dataset.spDuoMode = expanded ? 'duo' : 'single';
        this.offerShells.forEach((shell) => {
          shell.classList.toggle('is-selected', shell.dataset.spDuoOfferShell === this.selectedMode);
        });
        if (!expanded) this.setSecondOptionsExpanded(false);

        if (this.submitLabel) {
          this.submitLabel.textContent = expanded ? this.dataset.duoButtonLabel : this.singleButtonLabel;
        }
        this.syncStickyState();
        this.primaryHeading = this.querySelector('[data-sp-duo-primary-heading]');
        this.primaryGroups = [...this.querySelectorAll('[data-sp-duo-primary-group]')];
        this.firstHeightLabel = this.querySelector('[data-sp-duo-first-height-label]');
        if (this.primaryHeading) {
          this.primaryHeading.textContent = expanded
            ? this.translate('heightPerPillow', 'Height for each pillow')
            : this.translate('height', 'Height');
        }
        if (this.firstHeightLabel) {
          this.firstHeightLabel.hidden = !expanded;
        }
        this.primaryGroups.forEach((group) => {
          if (expanded && this.firstHeightLabel?.id) {
            group.setAttribute('aria-describedby', this.firstHeightLabel.id);
          } else {
            group.removeAttribute('aria-describedby');
          }
        });

        this.syncSecondHeightStatus();
        this.syncSecondHeightWarning();
      }

      syncFirstVariant(variantId = this.currentVariantId) {
        const variant = this.variants.find((item) => Number(item.id) === Number(variantId));

        if (variant && !this.secondHeightTouched) {
          const matchingSecondHeight = this.secondVariants.find(
            (input) => Number(input.value) === Number(variant.id) && !input.disabled
          );
          if (matchingSecondHeight) matchingSecondHeight.checked = true;
        }

        this.syncSecondHeightStatus();
        this.syncSecondHeightWarning();
      }

      syncSecondHeightStatus() {
        if (!this.secondStatus) return;
        const selectedVariant = this.variants.find(
          (item) => Number(item.id) === this.secondVariantId
        );
        const summary = selectedVariant?.label?.replace(/ (\d+(?:[.,]\d+)? cm)$/, ' · $1')
          || this.translate('chooseHeight', 'Choose a height');
        const sameHeight = Boolean(selectedVariant) && this.secondVariantId === this.currentVariantId;

        if (this.secondValue && this.secondSame) {
          this.secondValue.textContent = summary;
          this.secondSame.hidden = !sameHeight;
        } else {
          // Keep older cached markup usable during a theme asset update.
          this.secondStatus.textContent = sameHeight ? `${summary} · ${this.translate('sameAsFirst', 'Same as Pillow 1')}` : summary;
        }
      }

      syncSecondHeightWarning() {
        if (!this.highWarning || !this.highConfirm) return;
        const selectedVariant = this.variants.find(
          (item) => Number(item.id) === this.secondVariantId
        );
        const needsConfirmation = this.isDuo
          && Boolean(selectedVariant?.isHigh)
          && this.secondVariantId !== this.currentVariantId;

        this.highWarning.hidden = !needsConfirmation;
        this.highWarning.setAttribute('aria-hidden', needsConfirmation ? 'false' : 'true');
        if (!needsConfirmation) {
          this.highConfirm.checked = false;
          this.highConfirm.setAttribute('aria-invalid', 'false');
          if (this.highError) this.highError.hidden = true;
        }
      }

      validateFirstHeight(variant) {
        if (!variant?.isHigh) return true;

        const confirmations = [...document.querySelectorAll('[data-sp-height-13-confirm]')].filter(
          (confirm) => confirm.form === this.form || confirm.getAttribute('form') === this.form.id
        );
        if (!confirmations.length || confirmations.some((confirm) => confirm.checked)) return true;

        confirmations.forEach((confirm) => confirm.setAttribute('aria-invalid', 'true'));
        const warning = confirmations
          .map((confirm) => confirm.closest('[data-sp-height-13-warning]'))
          .find((item) => item && !item.hidden);
        const confirmation = warning?.querySelector('[data-sp-height-13-confirm]') || confirmations[0];

        warning?.classList.add('sp-height-warning--attention');
        warning?.scrollIntoView({ block: 'center', behavior: this.scrollBehavior });
        confirmation.focus({ preventScroll: true });
        return false;
      }

      validateSecondHeight() {
        const selectedVariant = this.variants.find(
          (item) => Number(item.id) === this.secondVariantId
        );
        if (
          !selectedVariant?.isHigh
          || this.secondVariantId === this.currentVariantId
          || this.highConfirm?.checked
        ) return true;

        if (this.highConfirm) {
          this.highConfirm.setAttribute('aria-invalid', 'true');
          this.highConfirm.focus({ preventScroll: true });
        }
        if (this.highError) this.highError.hidden = false;
        this.highWarning?.scrollIntoView({ block: 'center', behavior: this.scrollBehavior });
        return false;
      }

      get scrollBehavior() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      }

      getLineItemProperties() {
        const properties = {};
        const formData = new FormData(this.form);

        for (const [name, value] of formData.entries()) {
          const match = name.match(/^properties\[(.+)]$/);
          if (!match || typeof value !== 'string' || !value.trim()) continue;
          properties[match[1]] = value;
        }

        return properties;
      }

      handleFormSubmit = async (event) => {
        if (!this.isDuo) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.isBusy) return;

        const discountCode = (this.dataset.discountCode || '').trim();
        if (!discountCode) {
          this.showMessage(this.translate('errorUnavailable', 'The duo deal is temporarily unavailable. Please try again later.'), 'error');
          return;
        }

        // Snapshot the complete choice before the first request. A cart refresh or
        // accidental radio change must never turn one click into a partial duo.
        const firstVariantId = this.currentVariantId;
        const secondVariantId = this.secondVariantId;
        const firstVariant = this.variants.find((item) => Number(item.id) === firstVariantId);
        const secondVariant = this.variants.find((item) => Number(item.id) === secondVariantId);

        if (!this.validateFirstHeight(firstVariant) || !this.validateSecondHeight()) return;
        if (!firstVariant?.available || !secondVariant?.available) {
          this.showMessage(this.translate('errorOutOfStock', 'One of your selected heights is no longer in stock. Choose another height.'), 'error');
          return;
        }

        const groupId = globalThis.crypto?.randomUUID
          ? `sp_duo_${globalThis.crypto.randomUUID()}`
          : `sp_duo_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        const properties = {
          ...this.getLineItemProperties(),
          _sp_offer: 'duo_20',
          _sp_duo_id: groupId,
        };
        const items = firstVariantId === secondVariantId
          ? [{ id: firstVariantId, quantity: 2, properties }]
          : [
              { id: firstVariantId, quantity: 1, properties },
              { id: secondVariantId, quantity: 1, properties },
            ];

        this.setBusy(true);
        this.clearMessage();

        let addedLineKeys = [];
        let originalDiscountCodes = [];
        let addWasAttempted = false;
        let added;
        let updatedCart;
        let committed = false;
        const sections = this.getCartSections();

        try {
          const originalCart = await this.fetchCart();
          originalDiscountCodes = this.getDiscountCodes(originalCart);

          addWasAttempted = true;
          added = await this.postJson(FoxTheme.routes.cart_add_url, {
            items,
            sections,
            sections_url: window.location.pathname,
          });

          if (added.status) throw new Error(added.description || this.translate('errorAdd', "We couldn't add the pillows."));
          addedLineKeys = (added.items || []).map((item) => item.key).filter(Boolean);

          const discountCodes = this.mergeDiscountCodes(originalDiscountCodes, [discountCode]);
          updatedCart = await this.postJson(FoxTheme.routes.cart_update_url, {
            discount: discountCodes.join(','),
            sections,
            sections_url: window.location.pathname,
          });

          if (
            !this.isDiscountApplied(updatedCart, discountCode)
            || !this.cartContainsExpectedGroup(updatedCart, groupId, items)
            || !this.areApplicableCodesPreserved(originalCart, updatedCart, discountCode)
          ) {
            throw new Error(this.translate('errorDiscount', "We couldn't apply the discount, so nothing was added to your cart."));
          }

          committed = true;
        } catch (error) {
          const rollbackConfirmed = addWasAttempted && !committed
            ? await this.rollbackAddedGroup(groupId, addedLineKeys, originalDiscountCodes)
            : true;
          const errorMessage = rollbackConfirmed
            ? error.message || this.translate('errorRetry', "We couldn't add the duo deal. Please try again.")
            : this.translate('errorVerifyCart', 'Something went wrong. Check your cart before trying again.');

          this.showMessage(errorMessage, 'error');
          document.dispatchEvent(
            new CustomEvent('product-ajax:error', {
              detail: { errorMessage },
            })
          );
          return;
        } finally {
          this.setBusy(false);
        }

        // The cart transaction is committed. Presentation failures must never
        // remove products that Shopify already accepted.
        try {
          this.updateThemeCart(updatedCart, added);
          this.showMessage(this.translate('success', '2 pillows added with duo savings.'), 'success');
          this.openCartDrawer();
        } catch (uiError) {
          console.error('The cart was updated but could not be opened automatically.', uiError);
        }
      };

      getCartSections() {
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', {
            bubbles: true,
            detail: { sections },
          })
        );
        return sections;
      }

      async fetchCart() {
        const response = await fetch(FoxTheme.routes.cart_url, {
          ...FoxTheme.utils.fetchConfig('json', 'GET'),
          cache: 'no-store',
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(this.translate('errorCartLoad', "We couldn't load your cart."));
        return payload;
      }

      async postJson(url, body) {
        const response = await fetch(url, {
          ...FoxTheme.utils.fetchConfig('json', 'POST'),
          body: JSON.stringify(body),
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || this.translate('errorGeneric', 'Something went wrong.'));
        return payload;
      }

      getDiscountCodes(cart) {
        return (cart.discount_codes || [])
          .map((discount) => discount.code)
          .filter(Boolean);
      }

      normalizeDiscountCode(code) {
        return String(code || '').trim().toLocaleLowerCase('nl-NL');
      }

      mergeDiscountCodes(...codeLists) {
        const merged = new Map();
        codeLists.flat().filter(Boolean).forEach((code) => {
          const normalizedCode = this.normalizeDiscountCode(code);
          if (normalizedCode && !merged.has(normalizedCode)) merged.set(normalizedCode, code);
        });
        return [...merged.values()];
      }

      getSidePillowQuantity(cart) {
        const variantIds = new Set(this.variants.map((variant) => Number(variant.id)));
        return (cart.items || []).reduce((quantity, item) => {
          const variantId = Number(item.variant_id || item.id || 0);
          return variantIds.has(variantId) ? quantity + Number(item.quantity || 0) : quantity;
        }, 0);
      }

      getDiscountAmount(cart, code) {
        const normalizedCode = this.normalizeDiscountCode(code);

        const lineDiscount = (cart.items || []).reduce((cartTotal, item) => {
          const allocations = item.line_level_discount_allocations?.length
            ? item.line_level_discount_allocations
            : (item.discount_allocations || []);
          const itemTotal = allocations.reduce((allocationTotal, allocation) => {
            const title = this.normalizeDiscountCode(allocation.discount_application?.title);
            if (title !== normalizedCode) return allocationTotal;
            return allocationTotal + Number(allocation.amount || 0);
          }, 0);
          return cartTotal + itemTotal;
        }, 0);
        const cartDiscount = (cart.cart_level_discount_applications || []).reduce((total, application) => {
          const title = this.normalizeDiscountCode(application.title);
          if (title !== normalizedCode) return total;
          return total + Number(application.total_allocated_amount || 0);
        }, 0);

        return Math.max(lineDiscount, cartDiscount);
      }

      isDiscountApplied(cart, code) {
        const normalizedCode = this.normalizeDiscountCode(code);
        const codeIsApplicable = (cart.discount_codes || []).some(
          (discount) => discount.applicable !== false
            && this.normalizeDiscountCode(discount.code) === normalizedCode
        );
        const expectedDiscount = Number(this.dataset.expectedDiscountCents || 0);
        return this.getSidePillowQuantity(cart) >= 2
          && codeIsApplicable
          && expectedDiscount > 0
          && this.getDiscountAmount(cart, code) >= expectedDiscount;
      }

      areApplicableCodesPreserved(beforeCart, afterCart, duoCode) {
        const normalizedDuoCode = this.normalizeDiscountCode(duoCode);
        const requiredCodes = (beforeCart.discount_codes || [])
          .filter((discount) => discount.applicable !== false)
          .map((discount) => this.normalizeDiscountCode(discount.code))
          .filter((code) => code && code !== normalizedDuoCode);
        const applicableCodes = new Set(
          (afterCart.discount_codes || [])
            .filter((discount) => discount.applicable !== false)
            .map((discount) => this.normalizeDiscountCode(discount.code))
        );
        return requiredCodes.every((code) => applicableCodes.has(code));
      }

      cartContainsExpectedGroup(cart, groupId, requestedItems) {
        const expectedVariants = new Map();
        requestedItems.forEach((item) => {
          const variantId = Number(item.id || item.variant_id || 0);
          expectedVariants.set(variantId, (expectedVariants.get(variantId) || 0) + Number(item.quantity || 0));
        });
        const actualVariants = new Map();
        (cart.items || [])
          .filter((item) => item.properties?._sp_duo_id === groupId)
          .forEach((item) => {
            const variantId = Number(item.variant_id || item.id || 0);
            actualVariants.set(variantId, (actualVariants.get(variantId) || 0) + Number(item.quantity || 0));
          });

        return expectedVariants.size === actualVariants.size
          && [...expectedVariants].every(([variantId, quantity]) => actualVariants.get(variantId) === quantity);
      }

      async rollbackAddedGroup(groupId, fallbackLineKeys, originalDiscountCodes) {
        let lineKeys = [...new Set(fallbackLineKeys)];
        let rollbackConfirmed = false;
        const discountCode = (this.dataset.discountCode || '').trim();
        const normalizedDiscountCode = discountCode.toLocaleLowerCase('nl-NL');
        const originalHadDuoCode = originalDiscountCodes.some(
          (code) => code.toLocaleLowerCase('nl-NL') === normalizedDiscountCode
        );

        for (let attempt = 0; attempt < 2; attempt += 1) {
          let currentCart;
          try {
            currentCart = await this.fetchCart();
            lineKeys = (currentCart.items || [])
              .filter((item) => item.properties?._sp_duo_id === groupId)
              .map((item) => item.key)
              .filter(Boolean);
          } catch (_cartReadError) {
            // The fallback keys may still work. A second pass uses a fresh cart read.
          }

          const updates = Object.fromEntries(lineKeys.map((key) => [key, 0]));
          const currentDiscountCodes = currentCart ? this.getDiscountCodes(currentCart) : [];
          const anotherDuoGroupRemains = (currentCart?.items || []).some(
            (item) => item.properties?._sp_offer === 'duo_20' && item.properties?._sp_duo_id !== groupId
          );
          const safeCurrentCodes = currentDiscountCodes.filter(
            (code) =>
              code.toLocaleLowerCase('nl-NL') !== normalizedDiscountCode ||
              originalHadDuoCode ||
              anotherDuoGroupRemains
          );
          const restoredDiscountCodes = [...new Set([...originalDiscountCodes, ...safeCurrentCodes])];
          const rollbackBody = {};
          if (currentCart) rollbackBody.discount = restoredDiscountCodes.join(',');
          if (lineKeys.length) rollbackBody.updates = updates;

          try {
            await this.postJson(FoxTheme.routes.cart_update_url, rollbackBody);
          } catch (_rollbackError) {
            // Verify the server state below before deciding what the customer sees.
          }

          try {
            const verifiedCart = await this.fetchCart();
            lineKeys = (verifiedCart.items || [])
              .filter((item) => item.properties?._sp_duo_id === groupId)
              .map((item) => item.key)
              .filter(Boolean);
            const verifiedCodes = new Set(
              this.getDiscountCodes(verifiedCart).map((code) => this.normalizeDiscountCode(code))
            );
            const originalCodesRestored = originalDiscountCodes.every(
              (code) => verifiedCodes.has(this.normalizeDiscountCode(code))
            );
            const anotherDuoGroupStillExists = (verifiedCart.items || []).some(
              (item) => item.properties?._sp_offer === 'duo_20' && item.properties?._sp_duo_id !== groupId
            );
            const duoCodeStateRestored = originalHadDuoCode
              || anotherDuoGroupStillExists
              || !verifiedCodes.has(normalizedDiscountCode);
            if (!lineKeys.length && originalCodesRestored && duoCodeStateRestored) {
              rollbackConfirmed = true;
              break;
            }
          } catch (_verificationError) {
            // Without a read-back the rollback is not reported as successful.
          }
        }

        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { open: false },
          })
        );

        return rollbackConfirmed;
      }

      updateThemeCart(cart, addedProduct) {
        this.publishCartUpdate(cart);

        const addedItems = addedProduct.items || [addedProduct];
        const primaryItem = addedItems[0] || addedProduct;
        const totalQuantity = addedItems.reduce(
          (total, item) => total + Number(item.quantity || 0),
          0
        );
        const variantIds = [
          ...new Set(
            addedItems
              .map((item) => Number(item.variant_id || item.id || 0))
              .filter(Boolean)
          ),
        ];
        const product = {
          ...primaryItem,
          quantity: totalQuantity || Number(primaryItem.quantity || 1),
          items: addedItems,
          variant_ids: variantIds,
        };
        if (variantIds.length > 1) {
          delete product.id;
          delete product.variant_id;
        }

        document.dispatchEvent(
          new CustomEvent('product-ajax:added', {
            detail: { product },
          })
        );
      }

      publishCartUpdate(cart) {
        if (FoxTheme.pubsub?.publish && FoxTheme.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
        }
      }

      openCartDrawer() {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.show === 'function') {
          drawer.show(this.submitButton);
        } else {
          window.location.assign(FoxTheme.routes.cart_url);
        }
      }

      bindStickyBar() {
        this.stickyForm = document.querySelector('.sticky-atc-bar form[is="product-form"]');
        this.stickyButton = this.stickyForm?.querySelector('[name="add"]');
        this.stickyLabel = this.stickyButton?.querySelector('span');
        this.stickyOriginalLabel = this.stickyLabel?.textContent.trim() || this.translate('inCart', 'Add to cart');
        this.stickyOriginalType = this.stickyButton?.type;
        this.choiceStartId = this.querySelector('[data-sp-choice-start]')?.id;
        const stickyBar = this.stickyForm?.closest('.sticky-atc-bar');
        const summary = stickyBar?.querySelector('[data-sp-sticky-summary]');
        const quantity = summary?.querySelector('[data-sp-sticky-quantity]');
        const price = summary?.querySelector('[data-sp-sticky-price]');
        if (summary && quantity && price && this.stickyButton && this.dataset.stickyButtonLabel
          && this.dataset.stickySinglePrice && this.dataset.stickyDuoPrice) {
          this.stickySummary = summary;
          this.stickyQuantity = quantity;
          this.stickyPrice = price;
          this.stickyTotalLabel = summary.querySelector('[data-sp-sticky-total-label]');
          this.stickyButton.dataset.spStickyLabel = this.dataset.stickyButtonLabel;
          this.stickyButton.type = 'button';
          this.stickyButton.setAttribute('aria-describedby', summary.id);
          summary.hidden = false;
          stickyBar.setAttribute('data-sp-sticky-summary-ready', '');
          this.stickyButton.addEventListener('click', this.handleStickySubmit, true);
        }
        this.stickyForm?.addEventListener('submit', this.handleStickySubmit, true);
      }

      setBusy(isBusy) {
        this.isBusy = isBusy;
        [this.submitButton, this.stickyButton].filter(Boolean).forEach((button) => {
          button.classList.toggle('btn--loading', isBusy);
          if (isBusy) button.setAttribute('aria-disabled', 'true');
          else button.removeAttribute('aria-disabled');
        });
      }

      clearMessage() {
        if (!this.message) return;
        this.message.hidden = true;
        this.message.textContent = '';
        delete this.message.dataset.state;
      }

      showMessage(text, state) {
        if (!this.message) return;
        this.message.textContent = text;
        this.message.dataset.state = state;
        this.message.hidden = false;
      }
    }
  );
}
